import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel } from "@/components/Bits";
import { toast } from "sonner";
import { CheckCircle2, Circle, Copy, ArrowRight, X } from "lucide-react";

export default function OnboardingCard({ tenantId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["onboarding", tenantId], queryFn: () => api.get(`/onboarding/${tenantId}`).then((r) => r.data), enabled: !!tenantId && !user?.demo, refetchInterval: 15000 });
  const d = q.data;
  if (!d || d.dismissed || d.done === d.total) return null;
  const manager = user?.owner || user?.team_role === "admin";
  const copy = () => navigator.clipboard.writeText(d.installer).then(() => toast.success("Installer command copied — paste into an Admin PowerShell"));
  const dismiss = async () => { await api.post("/onboarding/dismiss"); qc.invalidateQueries({ queryKey: ["onboarding", tenantId] }); };
  const pct = Math.round((d.done / d.total) * 100);
  return (
    <Panel className="col-span-12" kicker="Getting started" title={`${d.done}/${d.total} steps complete`} right={manager && <button data-testid="onboarding-dismiss" onClick={dismiss} className="text-zinc-500 hover:text-white" aria-label="Hide checklist"><X size={14} /></button>}>
      <div data-testid="onboarding-card" className="p-4 space-y-3">
        <div className="h-1 bg-white/5"><div className="h-1 bg-cyan-400 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {d.steps.map((s, i) => (
            <div key={s.key} data-testid={`onboarding-step-${s.key}`} className={`border p-3 text-sm ${s.done ? "border-emerald-400/30 bg-emerald-400/[0.04]" : "hairline"}`}>
              <div className="flex items-start gap-2">
                {s.done ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" /> : <Circle size={16} className="text-zinc-600 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <div className="font-semibold leading-tight">{i + 1}. {s.title}</div>
                  <div className="text-[11px] text-zinc-500 mt-1">{s.done && s.count ? `${s.count} so far` : s.hint}</div>
                  {!s.done && s.to && <Link to={s.to} data-testid={`onboarding-go-${s.key}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-cyan-300 mt-2">Go <ArrowRight size={10} /></Link>}
                </div>
              </div>
            </div>
          ))}
        </div>
        {!d.steps[2].done && d.installer && (
          <div className="border border-cyan-400/30 bg-cyan-400/[0.04] p-3">
            <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-300 mb-1">Windows · run in an Admin PowerShell · nothing else to install</div>
            <div className="flex items-start gap-2"><code data-testid="onboarding-installer" className="font-mono text-[11px] text-zinc-100 break-all flex-1">{d.installer}</code>
              <button data-testid="onboarding-copy-installer" onClick={copy} className="text-zinc-400 hover:text-white"><Copy size={13} /></button></div>
          </div>
        )}
      </div>
    </Panel>
  );
}
